import React, { useEffect } from 'react';
import { isValidCoordinate } from '../utils/validation';
import { GeocodingResult, GeocodingCandidate } from '../types';

interface CandidateModalProps {
  isOpen: boolean;
  result: GeocodingResult | null;
  onClose: () => void;
  onSelect: (candidate: GeocodingCandidate) => void;
}

const CandidateModal: React.FC<CandidateModalProps> = ({
  isOpen,
  result,
  onClose,
  onSelect
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !result) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelect = (candidate: GeocodingCandidate) => {
    onSelect(candidate);
    onClose();
  };

  const allCandidates: (GeocodingCandidate & { isCurrent?: boolean })[] = [
    {
      formatted_address: result.formattedAddress,
      location: result.location,
      addressComponent: result.addressComponent,
      isCurrent: true
    },
    ...(result.candidates || [])
  ];

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content candidate-modal">
        <div className="modal-header">
          <h3 className="modal-title">选择正确的地址</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="original-address">
            <strong>原始输入:</strong> {result.originalAddress}
          </div>

          <div className="candidates-list">
            {allCandidates.map((candidate, index) => (
              <div
                key={index}
                className={`candidate-item ${candidate.isCurrent ? 'current' : ''}`}
                onClick={() => handleSelect(candidate)}
              >
                <div className="candidate-header">
                  <div className="candidate-address">
                    {candidate.formatted_address}
                  </div>
                  {candidate.isCurrent && (
                    <span className="current-badge">当前选择</span>
                  )}
                </div>

                <div className="candidate-details">
                  <div className="candidate-location">
                    {isValidCoordinate(candidate.location) ? (
                      <>经度: {candidate.location.lng.toFixed(6)}, 纬度: {candidate.location.lat.toFixed(6)}</>
                    ) : (
                      <span className="coordinate-error">坐标无效</span>
                    )}
                  </div>
                  {candidate.addressComponent && (
                    <div className="candidate-component">
                      {candidate.addressComponent.province}
                      {candidate.addressComponent.city}
                      {candidate.addressComponent.district}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            取消
          </button>
          <div className="modal-hint">
            点击上方地址选择，或按 ESC 键关闭
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateModal;
